import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Form, Input, Button, Card, Alert, message, Switch, Modal } from 'antd';
import { EditOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { Auth } from 'aws-amplify';
import { editUserBySupplierAdmin, logSupplierAdminApproval } from '../../graphql/mutations';
import { getUserInfo, getLicenseFee } from '../../graphql/queries';
import { axiosRequest } from '../../utils/axiosRequest';
import styles from './SupplierAdminPortal.module.css';

export const EditUserBySupplierAdmin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState(null);
  const [initialActive, setInitialActive] = useState(false);
  const [showCostModal, setShowCostModal] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  const [hasAcknowledgedCost, setHasAcknowledgedCost] = useState(false);
  const [licenseFee, setLicenseFee] = useState(null);

  const userToEdit = location.state?.user;

  useEffect(() => {
    if (!userToEdit) {
      message.error('No user selected for editing');
      navigate('/supplier-admin');
      return;
    }

    // Pre-fill form with existing user data
    form.setFieldsValue({
      email: userToEdit.mail,
      firstName: userToEdit.firstName,
      lastName: userToEdit.lastName,
      isActive: userToEdit.active,
    });
    setInitialActive(userToEdit.active);

    // Get license info
    Auth.currentSession()
      .then(async (session) => {
        const userEmail = session.getIdToken().payload.email;
        const headers = {
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*',
          Authorization: session.getIdToken().getJwtToken(),
        };

        // Get vendor group number
        const userInfoBody = {
          query: getUserInfo,
          variables: { mail: userEmail },
        };
        const userInfoResult = await axiosRequest({ headers, body: userInfoBody, method: 'post' });
        const userData = userInfoResult.data?.data?.getUserInfo?.data?.[0];

        if (userData?.vendorGroupNumber) {
          const feeBody = {
            query: getLicenseFee,
            variables: {},
          };
          const feeResult = await axiosRequest({ headers, body: feeBody, method: 'post' });
          const fee = feeResult.data?.data?.getLicenseFee?.licenseFee;
          setLicenseFee(fee);
        }
      })
      .catch((err) => {
        console.error('Error getting license info:', err);
      });
  }, [userToEdit, form, navigate]);

  const getHeaders = async () => {
    const session = await Auth.currentSession();
    const idToken = session.getIdToken();
    const jwt = idToken.getJwtToken();
    return {
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      Authorization: jwt,
    };
  };

  const handleAcknowledgeCost = async () => {
    try {
      const targetUserEmail = pendingFormData?.email?.toLowerCase();
      const headers = await getHeaders();
      const body = {
        query: logSupplierAdminApproval,
        variables: {
          userEmail: targetUserEmail,
        },
      };
      await axiosRequest({ headers, body, method: 'post' });
      setHasAcknowledgedCost(true);
      setShowCostModal(false);

      // Now submit the pending form
      if (pendingFormData) {
        await submitUser(pendingFormData);
      }
    } catch (error) {
      console.error('Error logging approval:', error);
      message.error('Failed to log approval. Please try again.');
    }
  };

  const submitUser = async (values) => {
    setLoading(true);
    setResponseMessage(null);
    setPendingFormData(null);

    try {
      const headers = await getHeaders();
      const body = {
        query: editUserBySupplierAdmin,
        variables: {
          email: values.email.toLowerCase(),
          firstName: values.firstName,
          lastName: values.lastName,
          isActive: values.isActive,
        },
      };
      const result = await axiosRequest({ headers, body, method: 'post' });

      const response = result.data?.data?.editUserBySupplierAdmin;

      if (response.success) {
        message.success(response.message);
        setResponseMessage({
          type: 'success',
          message: response.message,
          requiresApproval: response.requiresApproval,
        });

        // Navigate back after delay
        setTimeout(() => {
          navigate('/supplier-admin');
        }, 2000);
      } else {
        message.error(response.message);
        setResponseMessage({
          type: 'error',
          message: response.message,
        });
      }
    } catch (error) {
      console.error('Error updating user:', error);
      message.error('Failed to update user. Please try again.');
      setResponseMessage({
        type: 'error',
        message: 'Failed to update user. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    const isActivatingUser = !initialActive && values.isActive;

    if (isActivatingUser && !hasAcknowledgedCost) {
      setPendingFormData(values);
      setShowCostModal(true);
    } else {
      await submitUser(values);
    }
  };

  if (!userToEdit) {
    return null;
  }

  return (
    <div className={styles['supplier-admin-form']}>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/supplier-admin')}
        className={styles['mb-16']}
      >
        Back to Portal
      </Button>

      <Card
        title={
          <>
            <EditOutlined /> Edit User
          </>
        }
        className={styles['max-width-600']}
      >
        {!initialActive && (
          <Alert
            message="Activation Requires Approval"
            description="Activating an inactive user requires approval from an internal administrator. The user will remain inactive until approved."
            type="info"
            showIcon
            className={styles['mb-24']}
          />
        )}

        {responseMessage && (
          <Alert
            message={responseMessage.type === 'success' ? 'Success' : 'Error'}
            description={responseMessage.message}
            type={responseMessage.type}
            showIcon
            closable
            onClose={() => setResponseMessage(null)}
            className={styles['mb-24']}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Please enter email address' },
              { type: 'email', message: 'Please enter a valid email address' },
            ]}
          >
            <Input disabled placeholder="user@example.com" />
          </Form.Item>

          <Form.Item
            label="First Name"
            name="firstName"
            rules={[{ required: true, message: 'Please enter first name' }]}
          >
            <Input placeholder="John" />
          </Form.Item>

          <Form.Item
            label="Last Name"
            name="lastName"
            rules={[{ required: true, message: 'Please enter last name' }]}
          >
            <Input placeholder="Doe" />
          </Form.Item>

          <Form.Item
            label="Active Status"
            name="isActive"
            valuePropName="checked"
            extra={!initialActive ? "Activating this user requires internal admin approval" : ""}
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              Update User
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Modal
        width={620}
        title="License Cost Acknowledgment"
        open={showCostModal}
        onOk={handleAcknowledgeCost}
        onCancel={() => {
          setShowCostModal(false);
          setPendingFormData(null);
        }}
        okText="I Acknowledge"
        cancelText="Cancel"
      >
        <Alert
          message="Additional Cost Notice"
          description={licenseFee !== null
            ? `Activating this user will incur an annual cost of $${licenseFee.toFixed(2)}. Please confirm you understand and approve this charge.`
            : "Activating this user will incur an annual cost. Please confirm you understand and approve this charge."
          }
          type="warning"
          showIcon
          className={styles['mb-16']}
        />
        <p>By clicking "I Acknowledge", you confirm that you understand and approve the annual licensing costs for activating this user.</p>
      </Modal>
    </div>
  );
};
